<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("store_transfer_entry") }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/store/transfer/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="onSubmit">
        <!-- Header Form Section -->
        <div class="row gx-4 mb-3 transfer-fields">
          <div class="col-md-6 transfer-field">
            <label class="form-label small">{{ $t('voucher_no') }}:</label>
            <div class="transfer-control">
              <input type="text" class="form-control" v-model="form.voucher_no" readonly placeholder="—" />
              <div class="transfer-error"></div>
            </div>
          </div>

          <div class="col-md-6 transfer-field">
            <label class="form-label small">{{ $t('voucher_date') }}:</label>
            <div class="transfer-control">
              <input type="date" class="form-control" v-model="form.voucher_date" @change="fetchVoucherNo" />
              <div class="transfer-error"></div>
            </div>
          </div>

          <div class="col-md-6 transfer-field">
            <label class="form-label small">
              {{ $t('from_store') }}: <span class="text-danger">*</span>
            </label>
            <div class="transfer-control">
              <!-- Always fixed to the current (header) store for store users and admin -->
              <input type="text" class="form-control" :value="fromStoreName" readonly />
              <div class="transfer-error">
                <span v-if="errors.from_store_id">{{ errors.from_store_id }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6 transfer-field">
            <label class="form-label small">
              {{ $t('to_store') }}: <span class="text-danger">*</span>
            </label>
            <div class="transfer-control">
              <FormDropdown
                v-model="form.to_store_id"
                :options="formattedToStores"
                clearable
              />
              <div class="transfer-error">
                <span v-if="errors.to_store_id">{{ errors.to_store_id }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6 transfer-field">
            <label class="form-label small">{{ $t('select_item') }}:</label>
            <div class="transfer-control position-relative">
              <input
                ref="itemSearch"
                type="text"
                autocomplete="off"
                v-model="query"
                @input="onSearchInput"
                :placeholder="$t('search_item_by_name_barcode_serial')"
                class="form-control search-input-with-clear"
                @keydown="handleKeys"
              />
              <button
                v-if="query || results.length"
                type="button"
                class="search-clear"
                :title="$t('clear') || 'Clear'"
                @click="clearItemSearch"
              >
                <i class="fa fa-times" aria-hidden="true"></i>
              </button>

              <div
                v-if="results.length"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
              >
                <ul class="list-group list-group-flush">
                  <li
                    v-for="(item, index) in results"
                    :key="item.serialized_unit_id || `${item.variant_id || 0}-${item.id}-${index}`"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === itemIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                  >
                    <div class="flex-grow-1 d-flex flex-column" @click="selectItem(item)">
                      <div class="d-flex align-items-center gap-2">
                        <div style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;">
                          <img
                            :src="$getRowImageUrl(item)"
                            style="width:100%; height:100%; object-fit:cover;"
                          />
                        </div>
                        <div class="d-flex flex-column">
                          <strong class="text-dark">
                            {{ item.item_name }} - {{ item.item_code }}
                          </strong>
                          <span
                            v-if="item.serial_number"
                            style="font-size: 0.75rem; color: black;"
                          >
                            {{ $t('serial_number') }}: {{ item.serial_number }}
                          </span>
                          <span
                            v-if="!item.serialized_unit_id"
                            style="font-size: 0.75rem; color: black;"
                          >
                            Stock: {{ getAvailableStock(item) }}
                          </span>
                          <span
                            v-if="!item.serialized_unit_id"
                            style="font-size: 0.75rem; color: black;"
                          >
                            {{ $t('selling_price') }}: {{ formatMoney(item.selling_price ?? 0) }}
                          </span>
                          <template v-if="item.item_type_code === 'MULTI_VARIANT' || item.variant_id">
                            <span v-if="item.sku" style="font-size: 0.75rem; color: black;">
                              SKU: {{ item.sku }}
                            </span>
                            <span
                              v-if="item.variant_attributes?.length"
                              style="font-size: 0.75rem; color: black;"
                            >
                              {{
                                item.variant_attributes
                                  .map(v => `${v.attribute}: ${v.value}`)
                                  .join(' | ')
                              }}
                            </span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
              <div class="transfer-error"></div>
            </div>
          </div>

          <div class="col-md-6 transfer-field">
            <label class="form-label small">
              {{ $t('quantity') }}: <span class="text-danger">*</span>
            </label>
            <div class="transfer-control">
              <div class="input-group">
                <input
                  type="number"
                  class="form-control"
                  :placeholder="$t('quantity')"
                  v-model="form.quantity"
                  min="1"
                />
                <button type="button" class="btn btn-info transfer-add" @click="addItem">
                  {{ $t('add') }}
                </button>
              </div>
              <div class="transfer-error">
                <span v-if="errors.quantity">{{ errors.quantity }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="selectedItem?.item_type_code === 'WARRANTY'"
            class="col-md-12 transfer-field"
          >
            <label class="form-label small">
              {{ $t('serial_numbers') }}: <span class="text-danger">*</span>
            </label>
            <textarea
              class="form-control"
              rows="2"
              v-model="form.serial_numbers_text"
              :placeholder="$t('serial_numbers_hint')"
            ></textarea>
            <small class="text-muted">{{ $t('serial_pick_or_scan') }}</small>
          </div>
        </div>

        <div class="table-responsive mb-3 fixed-table">
          <table class="table table-bordered table-striped align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 50%">{{ $t('item_name') }}</th>
                <th style="width: 25%">{{ $t('quantity') }}</th>
                <th style="width: 25%">{{ $t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in addedItems" :key="index">
                <td>{{ row.item_name }}</td>
                <td>{{ row.quantity }}</td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeItem(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              <tr v-if="!addedItems.length">
                <td colspan="3" class="text-center text-muted">No Items added</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="row g-3 align-items-end mt-4">
          <div class="col-auto d-flex align-items-end ms-auto">
            <button
              type="button"
              class="btn btn-cancel me-2"
              :disabled="submitting"
              @click="cancelForm"
            >
              {{ $t('cancel') }}
            </button>
            <button type="submit" class="btn btn-submit" :disabled="submitting">
              {{ submitting ? $t('saving') : $t('submit') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiUrls from "@/urls";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce';
import { truncateMoney2, todayLocal } from '@/utils';

export default {
  name: 'StoreTransfer',
  components: {
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      form: {
        voucher_no: '',
        voucher_date: todayLocal(),
        from_store_id: '',
        to_store_id: '',
        quantity: '',
        serial_numbers_text: '',
      },
      errors: {},
      query: '',
      results: [],
      itemIndex: 0,
      selectedItem: null,
      addedItems: [],
      stores: [],
      currentUser: null,
      debouncedSearch: null,
      submitting: false,
    };
  },

  computed: {
    fromStoreName() {
      const store = (this.stores || []).find(
        s => String(s.id) === String(this.form.from_store_id)
      );
      return store?.name || '';
    },

    formattedToStores() {
      const fromId = this.form.from_store_id;
      return (this.stores || [])
        .filter(s => s.id !== undefined && s.name)
        .filter(s => !fromId || String(s.id) !== String(fromId))
        .map(s => ({
          id: s.id.toString(),
          text: s.name.toString(),
        }));
    },
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300);
  },

  async mounted() {
    await this.initUserAndStores();
    this.fetchVoucherNo();
  },

  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    resolveCurrentStoreId() {
      // The header has already checked its store against the stores this user may use.
      const headerStoreId = localStorage.getItem('store_id');
      if (headerStoreId) {
        return String(headerStoreId);
      }
      if (this.currentUser?.store) {
        return String(this.currentUser.store);
      }
      if (this.stores?.length) {
        return String(this.stores[0].id);
      }
      return '';
    },

    applyFromStore() {
      const storeId = this.resolveCurrentStoreId();
      if (!storeId) return;
      this.form.from_store_id = storeId;
      if (String(this.form.to_store_id) === String(storeId)) {
        this.form.to_store_id = '';
      }
    },

    async initUserAndStores() {
      try {
        const [userRes, storesRes] = await Promise.all([
          this.$axios.get(apiUrls.login_user.get),
          this.$axios.get(apiUrls.stores.get, {
            params: { for_transfer: 1 },
          }),
        ]);

        this.currentUser = userRes.data || null;
        this.stores = storesRes.data || [];
        this.applyFromStore();
      } catch (error) {
        console.error('Error initializing user/stores:', error);
        this.stores = [];
      }
    },

    async fetchVoucherNo() {
      try {
        const res = await this.$axios.get(apiUrls.store_transfer_number.get, {
          params: { voucher_date: this.form.voucher_date },
        });
        this.form.voucher_no = res.data?.voucher_no || '';
      } catch (error) {
        console.error('Error loading voucher number:', error);
        this.form.voucher_no = '';
      }
    },

    async fetchStores() {
      try {
        const res = await this.$axios.get(apiUrls.stores.get, {
          params: { for_transfer: 1 },
        });
        this.stores = res.data || [];
      } catch (error) {
        console.error('Error loading stores:', error);
        this.stores = [];
      }
    },

    onSearchInput() {
      if (!String(this.query || '').trim()) {
        this.results = [];
        this.itemIndex = 0;
        return;
      }
      this.debouncedSearch();
    },

    clearItemSearch({ keepFocus = true } = {}) {
      this.query = '';
      this.results = [];
      this.itemIndex = 0;
      if (keepFocus) {
        this.$nextTick(() => this.$refs.itemSearch?.focus());
      }
    },

    async searchItems(options = {}) {
      const listAll = Boolean(options && options.listAll);
      const q = String(this.query || '').trim();
      if (!listAll && !q) {
        this.results = [];
        return;
      }

      if (!this.form.from_store_id) {
        this.toast.warning(this.$t('select_from_store_first'));
        this.results = [];
        return;
      }

      try {
        const itemSearch = await this.$axios.get(apiUrls.item_search.get, {
          params: { q: listAll ? '' : q },
        });
        const searchResults = itemSearch.data.results || [];
        if (!listAll) {
          const exactSerial = searchResults.find(
            (r) =>
              r.serialized_unit_id
              && r.serial_number
              && String(r.serial_number).toLowerCase() === q.toLowerCase()
          );
          if (exactSerial) {
            await this.selectItem(exactSerial);
            return;
          }
        }

        const response = await this.$axios.get(apiUrls.batch_item_search.get, {
          params: { q: listAll ? '' : q },
        });
        let results = response.data.results || [];
        const serialHits = searchResults.filter((r) => r.serialized_unit_id && r.serial_number);
        if (serialHits.length) {
          const keys = new Set(results.map((r) => `i${r.id}`));
          for (const hit of serialHits) {
            const key = `s${hit.serialized_unit_id}`;
            if (!keys.has(key)) {
              results = [hit, ...results];
              keys.add(key);
            }
          }
        }

        this.results = results;
        this.itemIndex = 0;

        if (
          !listAll
          && results.length === 1
          && results[0].barcode_qr_code
          && results[0].barcode_qr_code === q
        ) {
          await this.selectItem(results[0]);
        }
      } catch (error) {
        console.error('Search error:', error);
        this.results = [];
      }
    },

    getAvailableStock(item) {
      if (!item) return 0;
      if (item.variant_id) {
        return Number(item.current_stock ?? 0);
      }
      return Number(
        item.stock_quantity ??
        item.stock_qty ??
        item.current_stock ??
        0
      );
    },

    formatItemLabel(item) {
      if (!item) return '';
      let label = item.item_name || '';
      const parts = [];
      if (item.sku) {
        parts.push(`SKU: ${item.sku}`);
      }
      if (item.variant_attributes?.length) {
        parts.push(
          item.variant_attributes
            .map(v => `${v.attribute}: ${v.value}`)
            .join(' | ')
        );
      }
      if (parts.length) {
        label = `${label} (${parts.join(' — ')})`;
      }
      return label;
    },

    selectItem(item) {
      if (
        (item.item_type_code === 'MULTI_VARIANT' || item.has_variants)
        && !item.variant_id
        && !item.variant_attributes
        && !item.serialized_unit_id
      ) {
        this.loadVariants(item.id || item.item_id);
        return;
      }

      this.selectedItem = item;
      this.query = item.serial_number
        ? `${item.item_name} [${item.serial_number}]`
        : item.item_name;
      if (item.serial_number) {
        this.form.serial_numbers_text = String(item.serial_number);
        this.form.quantity = 1;
      }
      this.results = [];
      this.itemIndex = 0;
    },

    async loadVariants(itemId) {
      try {
        const response = await this.$axios.get(`/item/${itemId}/variants/`);
        this.results = response.data.results || [];
        this.itemIndex = 0;
      } catch (error) {
        console.error(error);
      }
    },

    async handleKeys(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.results.length) {
          await this.searchItems({ listAll: true });
          return;
        }
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selectedProduct = this.results[this.itemIndex];
        if (selectedProduct) {
          this.selectItem(selectedProduct);
        }
        event.preventDefault();
      } else if (event.key === 'Escape') {
        this.clearItemSearch();
        event.preventDefault();
      }
    },

    parseSerialNumbers(text) {
      return String(text || '')
        .replace(/,/g, '\n')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    },

    async resolveSerialIds(itemId, variantId, text, qty) {
      const serials = this.parseSerialNumbers(text);
      if (serials.length !== Number(qty)) {
        throw new Error(`Provide exactly ${qty} serial number(s).`);
      }
      const { data } = await this.$axios.get(apiUrls.serialized_units.get, {
        params: {
          item_id: itemId,
          variant_id: variantId || undefined,
          status: 'in_stock',
        },
      });
      const units = data.results || [];
      const bySerial = new Map(units.map((u) => [String(u.serial_number).toLowerCase(), u.id]));
      const ids = [];
      for (const serial of serials) {
        const id = bySerial.get(String(serial).toLowerCase());
        if (!id) {
          throw new Error(`Serial not found in stock: ${serial}`);
        }
        ids.push(id);
      }
      return ids;
    },

    async addItem() {
      this.errors = {};

      if (!this.form.from_store_id) {
        this.toast.warning(this.$t('select_from_store_first'));
        this.errors.from_store_id = this.$t('error.required');
        return;
      }

      if (
        this.form.to_store_id &&
        String(this.form.from_store_id) === String(this.form.to_store_id)
      ) {
        this.toast.error(this.$t('same_store_transfer_error'));
        this.errors.to_store_id = this.$t('same_store_transfer_error');
        return;
      }

      if (!this.selectedItem) {
        this.toast.warning(this.$t('select_item_first'));
        return;
      }

      const qty = Number(this.form.quantity || 0);
      if (!qty || qty <= 0) {
        this.errors.quantity = this.$t('error.required');
        this.toast.warning(this.$t('enter_valid_quantity'));
        return;
      }

      const availableStock = this.getAvailableStock(this.selectedItem);
      if (qty > availableStock) {
        this.toast.error(
          this.$t('insufficient_stock_detail', {
            available: availableStock,
            required: qty,
          })
        );
        this.errors.quantity = this.$t('insufficient_stock');
        return;
      }

      const currentItemId = this.selectedItem.item_id || this.selectedItem.id;
      const currentVariantId = this.selectedItem.variant_id || null;

      const alreadyExists = this.addedItems.some((row) => {
        if (currentVariantId) {
          return row.variant_id === currentVariantId;
        }
        return row.item_id === currentItemId && !row.variant_id;
      });

      if (alreadyExists) {
        this.toast.warning(this.$t('item_already_added'));
        this.resetItemInput();
        return;
      }

      let serialIds = [];
      if (this.selectedItem.item_type_code === 'WARRANTY') {
        try {
          serialIds = await this.resolveSerialIds(
            currentItemId,
            currentVariantId,
            this.form.serial_numbers_text,
            qty,
          );
        } catch (err) {
          this.toast.warning(err.message || String(err));
          return;
        }
      }

      this.addedItems.push({
        item_id: currentItemId,
        variant_id: currentVariantId,
        item_account_id: this.selectedItem.item_account_id || null,
        item_name: this.formatItemLabel(this.selectedItem),
        quantity: qty,
        stock_quantity: availableStock,
        serial_ids: serialIds,
        item_type_code: this.selectedItem.item_type_code,
      });

      this.resetItemInput();
    },

    resetItemInput() {
      this.query = '';
      this.results = [];
      this.selectedItem = null;
      this.itemIndex = 0;
      this.form.quantity = '';
      this.form.serial_numbers_text = '';
      this.errors = {};
    },

    removeItem(index) {
      this.addedItems.splice(index, 1);
    },

    validateForm() {
      this.errors = {};

      if (!this.form.from_store_id) {
        this.errors.from_store_id = this.$t('error.required');
      }
      if (!this.form.to_store_id) {
        this.errors.to_store_id = this.$t('error.required');
      }
      if (
        this.form.from_store_id &&
        this.form.to_store_id &&
        String(this.form.from_store_id) === String(this.form.to_store_id)
      ) {
        this.errors.to_store_id = this.$t('same_store_transfer_error');
        this.toast.error(this.$t('same_store_transfer_error'));
      }
      if (!this.form.voucher_no) {
        this.toast.warning(this.$t('voucher_number_missing'));
        return false;
      }
      if (!this.addedItems.length) {
        this.toast.warning(this.$t('add_at_least_one_item'));
        return false;
      }

      return Object.keys(this.errors).length === 0;
    },

    async onSubmit() {
      if (this.submitting) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      const payload = {
        voucher_no: this.form.voucher_no,
        voucher_date: this.form.voucher_date,
        from_store_id: this.form.from_store_id,
        to_store_id: this.form.to_store_id,
        items: this.addedItems.map((row) => ({
          item_id: row.item_id,
          variant_id: row.variant_id || null,
          quantity: row.quantity,
          serial_ids: row.serial_ids || [],
        })),
      };

      this.submitting = true;
      try {
        await this.$axios.post(apiUrls.store_transfer.post, payload);
        this.toast.success(this.$t('store_transfer_saved'));
        this.addedItems = [];
        this.resetItemInput();
        await this.fetchVoucherNo();
        this.$router.push('/store/transfer/list');
      } catch (error) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.detail ||
          this.$t('store_transfer_save_failed');
        this.toast.error(message);
        // Refresh voucher in case of duplicate voucher race
        await this.fetchVoucherNo();
        console.error('Store transfer submit error:', error);
      } finally {
        this.submitting = false;
      }
    },

    cancelForm() {
      this.$router.push('/store/transfer/list');
    },
  },
};
</script>

<style scoped>
.transfer-field {
  display: flex;
  align-items: flex-start;
}

.transfer-field > .form-label {
  flex: 0 0 150px;
  margin: 0 1rem 0 0;
  line-height: 38px;
}

.transfer-control {
  flex: 1 1 auto;
  min-width: 0;
}

.transfer-fields .form-control,
.transfer-fields :deep(.form-basic-dropdown > .btn),
.transfer-add {
  height: 38px;
  min-height: 38px;
}

.transfer-fields :deep(.form-basic-dropdown) {
  width: 100%;
}

.transfer-add {
  min-width: 80px;
}

.transfer-error {
  min-height: 1.4em;
  margin-top: 0.15rem;
  color: var(--bs-danger);
  font-size: 0.8rem;
}

.search-input-with-clear {
  padding-right: 2.5rem;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.65rem;
  height: 1.65rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #495057;
  color: #fff;
  line-height: 1;
}

.search-clear:hover {
  background: #212529;
  color: #fff;
}
</style>
